import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Storage key for the JWT access token. Stored client-side so the
// `Authorization: Bearer` header can be sent on every request — required for
// Safari (iOS/macOS) where Intelligent Tracking Prevention blocks
// cross-site auth cookies between the Vercel frontend and the Railway backend.
const TOKEN_KEY = "rwo_auth_token";

export function getAuthToken() {
    try {
        return window.localStorage.getItem(TOKEN_KEY) || "";
    } catch {
        return "";
    }
}

export function setAuthToken(token) {
    try {
        if (token) {
            window.localStorage.setItem(TOKEN_KEY, token);
        } else {
            window.localStorage.removeItem(TOKEN_KEY);
        }
    } catch {
        // noop — SSR / storage disabled / private mode
    }
}

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// --- Auth header + beta gate header injector --------------------------
// Stamps the persisted JWT (Authorization: Bearer) AND the beta access
// code on every request. The Bearer header is critical for Safari users
// where cross-site cookies are blocked by ITP.
api.interceptors.request.use((config) => {
    try {
        const token = window.localStorage.getItem(TOKEN_KEY) || "";
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        const code = window.localStorage.getItem("rwo_beta_code") || "";
        if (code) config.headers["X-Beta-Access"] = code;
    } catch {
        // noop — SSR or storage disabled
    }
    return config;
});

// --- Tier-gate interceptor --------------------------------------------
// When the backend responds with 403 { code: "tier_required", required_tier, feature },
// the PaywallProvider subscribes to this handler and pops the shared paywall modal.
// Keeping the handler on the module (rather than inside a React effect) means
// any caller — axios request, direct fetch wrapper, etc. — triggers the
// same paywall UX without every component needing to try/catch.
let paywallHandler = null;
export function setPaywallHandler(handler) {
    paywallHandler = handler;
}

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const detail = error?.response?.data?.detail;
        if (
            error?.response?.status === 403 &&
            detail &&
            typeof detail === "object" &&
            detail.code === "tier_required" &&
            paywallHandler
        ) {
            paywallHandler({
                required_tier: detail.required_tier,
                feature: detail.feature,
                message: detail.message,
            });
        }
        return Promise.reject(error);
    }
);

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (typeof detail === "object") {
        if (detail.code === "account_suspended") {
            const base = detail.message || "Your account has been suspended.";
            return detail.reason ? `${base} Reason: ${detail.reason}` : base;
        }
        if (detail.code === "tier_required") {
            return detail.message || `Upgrade to ${detail.required_tier} to continue.`;
        }
        if (typeof detail.msg === "string") return detail.msg;
        if (typeof detail.message === "string") return detail.message;
    }
    return String(detail);
}
